import type { AccountEntities } from "@/domain/entities/Account";
import type {
  ClientLocation,
  DetailClientManagement
} from "@/domain/entities/ClientManagement";
import type { ClientBranchSelectionV4 } from "@/domain/entities/BookingV4Client";
import { DistrictData } from "@/domain/entities/Location";
import type { ClientNamePOSData } from "@/domain/entities/Partner";

export const BookingClientActor = {
  CLIENT: "client",
  INTERNAL: "internal",
  POS: "pos"
} as const;

export type BookingClientActorValue = typeof BookingClientActor[keyof typeof BookingClientActor];

type AccountLocationData = {
  district_id?: number;
  district_code?: string;
  district_name?: string;
  city_id?: number;
  city_code?: string;
  city_name?: string;
  country_code?: string;
  country?: { code?: string };
  city_free_trade_zone?: string;
  origin_list_zip_code?: string[];
};

type ClientWithFtz = ClientNamePOSData & {
  isFtz?: boolean | string;
};

export function resolveBookingClientActor(
  account: AccountEntities
): BookingClientActorValue {
  if (account.isInternalAccount) return BookingClientActor.INTERNAL;
  if (account.isPosAccount) return BookingClientActor.POS;
  return BookingClientActor.CLIENT;
}

export function shouldShowClientPicker(account: AccountEntities): boolean {
  const actor = resolveBookingClientActor(account);
  return (
    actor === BookingClientActor.POS || actor === BookingClientActor.INTERNAL
  );
}

function normalizeBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function normalizeFtz(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value !== "string") return undefined;
  if (value.toLowerCase() === "yes") return true;
  if (value.toLowerCase() === "no") return false;
  return undefined;
}

function normalizeString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  return value || undefined;
}

function toListClientDistrict(client: ClientNamePOSData): DistrictData {
  return new DistrictData({
    id: client.clientDistrictId || 0,
    code: client.clientDistrictCode,
    name: client.clientDistrictName,
    cityName: client.clientOriginCity,
    countryCode: client.clientCountryCode,
    originListZipCode: client.clientDistrictOriginZipCode
  });
}

function toDetailDistrict(detail: DetailClientManagement): DistrictData {
  return new DistrictData({
    id: detail.clientDistrict.id,
    code: detail.clientDistrict.code,
    name: detail.clientDistrict.name,
    cityId: detail.clientCity.id,
    cityCode: detail.clientCity.code,
    cityName: detail.clientCity.name,
    countryCode:
      detail.clientDistrict.countryCode ||
      detail.clientCity.countryCode ||
      detail.clientCountry,
    originListZipCode: detail.clientDistrict.originListZipCode
  });
}

export function toAccountDistrict(location?: AccountLocationData): DistrictData {
  const zipCodes = location?.origin_list_zip_code;
  return new DistrictData({
    id: location?.district_id || 0,
    code: location?.district_code || "",
    name: location?.district_name || "",
    cityId: location?.city_id || 0,
    cityCode: location?.city_code || "",
    cityName: location?.city_name || "",
    countryCode: location?.country_code || location?.country?.code || "",
    isFtz: location?.city_free_trade_zone || "",
    originListZipCode: Array.isArray(zipCodes) ? zipCodes : []
  });
}

export function toClientBranchSelection(
  client: ClientNamePOSData,
  detail?: DetailClientManagement
): ClientBranchSelectionV4 {
  const district = detail
    ? toDetailDistrict(detail)
    : toListClientDistrict(client);
  const detailCodAmount = detail?.clientCodConfigAmount?.value;
  const sourceFtz = detail?.isFtz ?? (client as ClientWithFtz).isFtz;

  return {
    ...client,
    clientId: client.clientId || detail?.id || 0,
    clientCode: client.clientCode || detail?.clientCode || "",
    clientName: detail?.clientName || client.clientName,
    clientAlias: detail?.clientAlias || client.clientAlias,
    clientPhone: detail?.clientPhone || client.clientPhone,
    clientAddress: detail?.clientAddress || client.clientAddress,
    clientDistrict: district,
    clientCity: detail?.clientCity,
    originListZipCode: district.originListZipCode,
    clientCountry: detail?.clientCountry || client.clientCountryCode,
    clientCountryCode: detail?.clientCountry || client.clientCountryCode,
    isFtz: normalizeFtz(sourceFtz),
    clientIsDO: normalizeBoolean(detail?.clientIsDo ?? client.clientIsDO),
    clientIsCOD: normalizeBoolean(detail?.clientIsCod ?? client.clientIsCOD),
    clientIsDfod: normalizeBoolean(detail?.clientIsDfod ?? client.clientIsDfod),
    isShowShippingRate: normalizeBoolean(
      detail?.isShowShippingRate ?? client.isShowShippingRate
    ),
    clientCodConfigAmount:
      normalizeString(detailCodAmount) ||
      normalizeString(client.clientCodConfigAmount)
  } as ClientBranchSelectionV4;
}

export function toProfileClientBranch(
  account: AccountEntities,
  district: DistrictData
): ClientBranchSelectionV4 {
  const detail = account.account_type_detail;
  return {
    clientId: detail.id,
    clientCode: detail.code,
    clientName: detail.company_name || account.name,
    clientAlias: "",
    clientCategory: "",
    clientPackageType: "",
    clientPhone: detail.phone || account.phone_number,
    clientAddress: detail.address,
    clientDistrict: district,
    clientCity: {
      id: district.cityId,
      code: district.cityCode,
      name: district.cityName,
      countryCode: district.countryCode
    } as ClientLocation,
    originListZipCode: district.originListZipCode,
    clientCountry: district.countryCode || detail.countryCode,
    clientCountryCode: district.countryCode || detail.countryCode,
    clientOriginCity: district.cityName,
    isFtz: normalizeFtz(district.isFtz),
    clientIsDO: normalizeBoolean(detail.isDO),
    clientIsCOD: normalizeBoolean(detail.isCod),
    clientIsDfod: normalizeBoolean(detail.isDfod),
    isShowShippingRate: normalizeBoolean(detail.isShowShippingRate),
    clientCodConfigAmount: normalizeString(detail.codConfigAmount)
  } as ClientBranchSelectionV4;
}
